/**
 * Ticket generation from the requirements register
 * (CR-BUILD-BOARD-EPICS-2026-09-26, AC-19): one `generate-tickets` run at a
 * time per project, its SSE frames folded into a small progress state the
 * `GenerateTasksPanel` renders.
 *
 * INV-R.8 — a stream writes only for the request that opened it: every frame
 * carries the thunk's `requestId` + `projectId` and is dropped unless both
 * still match; the panel aborts its run on unmount, and `generationClosed`
 * resets EVERYTHING (phase and requestId included) so a late frame finds no
 * owner. INV-R.9 — a generation outcome lands here and nowhere else: never
 * `requirements.blocked`, never `board.blocked`.
 *
 * The reducers are pure; the stream itself lives in the thunk (via the
 * service). The board needs no nudge from here: generated rows are written
 * through the board API, which publishes `task.created` → the existing
 * `realtime/invalidateBoard` row.
 */

import { createAsyncThunk, createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { isClientSideRole } from '@/access/roles'
import { selectActiveRoleKey, selectSessionPrincipalKey } from '@/features/auth/slices/sessionSlice'
import { selectCanWriteProject, selectProjectMembers } from '@/features/projects/slices/projectDetailSlice'
import { selectVersionsListErrorFor, selectVersionsLoadedKey } from '@/features/versions/slices/versionsSlice'
import { isApiError, toApiError } from '@/services'
import type { SseFrame } from '@/services/sseFrames'
import { ticketGenerationService } from '../services/ticketGenerationService.http'
import type { GenerationEpicLine, GenerationFailure, GenerationPhase, GenerationSummary } from '../types'

export interface TicketGenerationState {
  /** The project the current (or last) run belongs to. */
  projectId: string | null
  /** The `generateTasks` request whose frames may write; `null` once closed. */
  requestId: string | null
  phase: GenerationPhase
  /** `ticket.delta` frames received — proof the stream is live, not a percentage. */
  progressTicks: number
  epics: GenerationEpicLine[]
  summary: GenerationSummary | null
  failure: GenerationFailure | null
}

const initialState: TicketGenerationState = {
  projectId: null,
  requestId: null,
  phase: 'idle',
  progressTicks: 0,
  epics: [],
  summary: null,
  failure: null
}

interface WithGeneration {
  ticketGeneration: TicketGenerationState
}

export interface GenerateTasksArg {
  orgId: string
  projectId: string
  /** The active version's id (`activeVersionOf`), the `:versionId` of the route. */
  versionSlug: string
}

/** The frame a stream delivers, stamped with the request and project that opened it. */
export interface GenerationFrame {
  requestId: string
  projectId: string
  frame: SseFrame
}

/** A rejection that is an abort (unmount), not a failure — it writes nothing. */
const ABORTED = 'aborted'

/** The failure a stream that closed without a terminal frame is recorded as. */
export const STREAM_ENDED = 'stream.ended'

/**
 * The reason code of a server `reason` / error message: the text before the
 * first `:` (`"generation.ai.unavailable: no provider ready"` →
 * `generation.ai.unavailable`), or the whole trimmed text when it has none.
 */
export const reasonCode = (reason: string): string => {
  const head = reason.split(':', 1)[0]?.trim() ?? ''
  return head === '' ? 'generic' : head
}

/**
 * Open one generation stream for the active version. The body is `{}` — never
 * `force` (spec R.6.5). Guarded: a second start for a project whose run is
 * still streaming is vetoed (keyed by project, so another project's run never
 * blocks this one).
 */
export const generateTasks = createAsyncThunk<void, GenerateTasksArg, { rejectValue: GenerationFailure }>(
  'ticketGeneration/generate',
  async ({ orgId, projectId, versionSlug }, { dispatch, requestId, signal, rejectWithValue }) => {
    try {
      await ticketGenerationService.generate(
        orgId,
        projectId,
        versionSlug,
        (frame) => {
          if (!signal.aborted) dispatch(generationFrameReceived({ requestId, projectId, frame }))
        },
        signal
      )
    } catch (error) {
      if (signal.aborted) return rejectWithValue({ code: ABORTED, message: ABORTED })
      const apiError = isApiError(error) ? error : toApiError(error)
      return rejectWithValue({
        code: apiError.backendKey ?? String(apiError.status),
        message: apiError.message,
        status: apiError.status > 0 ? apiError.status : undefined
      })
    }
  },
  {
    condition: ({ projectId }, { getState }) => {
      const { ticketGeneration } = getState() as WithGeneration
      return !(ticketGeneration.phase === 'streaming' && ticketGeneration.projectId === projectId)
    }
  }
)

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const numberOf = (data: Record<string, unknown>, key: string): number => {
  const value = data[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

const stringOf = (data: Record<string, unknown>, key: string): string => {
  const value = data[key]
  return typeof value === 'string' ? value : ''
}

export const ticketGenerationSlice = createSlice({
  name: 'ticketGeneration',
  initialState,
  reducers: {
    /** One SSE frame of a run arrived (see INV-R.8 in the header). */
    generationFrameReceived(state, action: PayloadAction<GenerationFrame>) {
      const { requestId, projectId, frame } = action.payload
      if (state.requestId !== requestId || state.projectId !== projectId) return
      // A terminal frame closes the run: a trailing `error` (the server sends
      // one right after `generation.failed`) or any late frame writes nothing.
      if (state.phase !== 'streaming') return
      const data = isRecord(frame.data) ? frame.data : {}
      switch (frame.event) {
        case 'ticket.delta':
          state.progressTicks += 1
          break
        case 'ticket.completed':
          state.epics.push({
            epicId: stringOf(data, 'epicId'),
            title: stringOf(data, 'title'),
            storyCount: numberOf(data, 'storyCount')
          })
          break
        case 'generation.completed':
          state.summary = {
            count: numberOf(data, 'count'),
            cached: data['cached'] === true,
            requirementCount: numberOf(data, 'requirementCount'),
            skippedRequirementCount: numberOf(data, 'skippedRequirementCount'),
            archivedStaleCount: numberOf(data, 'archivedStaleCount'),
            durationMs: numberOf(data, 'durationMs')
          }
          state.phase = 'completed'
          break
        case 'generation.failed': {
          const reason = stringOf(data, 'reason')
          state.failure = { code: reasonCode(reason), message: reason }
          state.phase = 'failed'
          break
        }
        case 'error': {
          const message = stringOf(data, 'message')
          state.failure = { code: reasonCode(message), message }
          state.phase = 'failed'
          break
        }
        default:
          // `generation.started`, `keepalive` and unknown names change nothing.
          break
      }
    },
    /** The panel closed (unmount / project change): forget the run entirely. */
    generationClosed() {
      return initialState
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateTasks.pending, (state, action) => {
        state.projectId = action.meta.arg.projectId
        state.requestId = action.meta.requestId
        state.phase = 'streaming'
        state.progressTicks = 0
        state.epics = []
        state.summary = null
        state.failure = null
      })
      .addCase(generateTasks.fulfilled, (state, action) => {
        // The server closed the stream. Without a terminal frame the outcome is
        // unknown: say so rather than show a run still "generating" forever.
        if (state.requestId !== action.meta.requestId || state.phase !== 'streaming') return
        state.failure = { code: STREAM_ENDED, message: STREAM_ENDED }
        state.phase = 'failed'
      })
      .addCase(generateTasks.rejected, (state, action) => {
        if (action.meta.aborted || action.meta.condition || action.payload?.code === ABORTED) return
        if (state.requestId !== action.meta.requestId || state.phase !== 'streaming') return
        state.failure = action.payload ?? { code: 'generic', message: action.error.message ?? '' }
        state.phase = 'failed'
      })
  }
})

export const { generationFrameReceived, generationClosed } = ticketGenerationSlice.actions

/* ── Selectors ──────────────────────────────────────────────────────────── */

export const selectGeneration = (s: WithGeneration): TicketGenerationState => s.ticketGeneration

/** The run state when it belongs to `projectId`, else `null` — another project's run never renders here. */
export const selectGenerationFor = createSelector(
  [selectGeneration, (_s: WithGeneration, projectId: string) => projectId],
  (generation, projectId): TicketGenerationState | null => (generation.projectId === projectId ? generation : null)
)

/**
 * INV-R.7 — the Generate action exists only for a firm-side writer, and fails
 * closed: the project detail must grant `permissions.write`, the caller's own
 * member row (if any) must not be CLIENT-side, and the session role must not
 * be client-side. An org admin with write and no member row qualifies. The
 * server refuses the same callers (D3); this only decides what renders.
 */
export const selectCanGenerateTasks = createSelector(
  [selectCanWriteProject, selectProjectMembers, selectSessionPrincipalKey, selectActiveRoleKey],
  (canWrite, members, principalKey, role): boolean =>
    canWrite &&
    !isClientSideRole(role) &&
    !members.some((member) => member.userId !== undefined && member.userId === principalKey && member.side === 'CLIENT')
)

/** Where the versions read the Generate card depends on stands for one project. */
export type GenerationVersionsRead = 'loading' | 'failed' | 'loaded'

type WithVersionsRead = Parameters<typeof selectVersionsLoadedKey>[0]

/**
 * Fix cycle 1, M2: the Generate card must tell "the versions are still
 * loading" and "the versions read failed" apart from "this project has no
 * version" — the last only once the read for THIS project succeeded (an
 * empty list). A failure recorded for another project never counts.
 */
export const selectGenerationVersionsRead = (s: WithVersionsRead, projectId: string): GenerationVersionsRead =>
  selectVersionsListErrorFor(s, projectId) !== null
    ? 'failed'
    : selectVersionsLoadedKey(s) === projectId
      ? 'loaded'
      : 'loading'
